import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useHistory } from 'react-router-dom';
import {
  Player,
  BigPlayButton,
  Shortcut,
  ControlBar,
  PlayToggle,
  VolumeMenuButton,
  PlaybackRateMenuButton,
} from 'video-react';

import styles from './EduDetail.module.less';
import {
  getEducationDetail,
  getEducationPlayUrl,
  postEducationPlayDataReport,
  postEducationCollectReport,
} from '../../../actions/education';
import { CONTEXT } from '../../../config';
import { getQueryString, logNative, localGetItem } from '../../../utils/utils';
import Loading from '../../../components/Loading/Loading';
import SoftCardEdu from '../../../components/SoftCardEdu/SoftCardEdu';
import { education } from '../../../constants/storageKeys';
import { logging, logPostInfo } from '../../../actions/log';
import EduDetailPay from './EduDetailPay';

const selectPayUrl = createSelector(
  state => state.education,
  education => education.payUrl,
);
const selectDetails = createSelector(
  state => state.education,
  education => education.detialInfo,
);
const selectCollect = createSelector(
  state => state.education,
  education => education.collect,
);
const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectUserInfo = createSelector(
  state => state.user,
  user => user.userInfo,
);

// 定义一些变量, 由于Subscribe订阅函数，无法获取已经变化的数据问题，所以将这些数据保存为js中局部变量
let EduVoideIsEnd = false;
let EduVoideIsReport = false;
let EduVoideCollected = false; // 当前课程是否已经点击收藏

let EduVoidePlayingChapterUrl = null; // 正在播放的选中章节的视频参数
let EduVoidePlayingChapter = null; // 选中的章节
let EduVoideIsClick = false; // 是否为点击右侧章节播放
let EduVoideChapterList = null; // 此课程的章节数组

export default function EduDetail() {
  const dispatch = useDispatch();
  const history = useHistory();

  const courseId = getQueryString('courseId');
  // studyState 0:进入详情页面，1： 进入详情页面并顺序播放， 2：进入详情页面重头播放
  const studyState = getQueryString('studyState') || '0';

  const logInfoData = useSelector(selectLogInfo);
  const detailsData = useSelector(selectDetails); // 详情数据
  const payUrlData = useSelector(selectPayUrl); // 当前播放视频的url
  const collectData = useSelector(selectCollect); // 当前播放课程收藏
  const userInfo = useSelector(selectUserInfo); // 用户信息

  const [isAutoPlay, setIsAutoPlay] = useState(true); // 控制是否是直接开始播放
  const [player, setPlayer] = useState(null);
  const [isPause, setIsPause] = useState(true); // 是否暂停
  // const [isReplay, setIsReplay] = useState(false); // 是否重新播放
  const [isAllVideoEnd, setIsAllVideoEnd] = useState(false); // 是否重新播放
  const [payVisible, setPayVisible] = useState(false); // 是否显示弹框
  const [isNeedPay, setIsNeedPay] = useState(false); // 是否需要付费

  // 获取本地记录的播放进度
  const VideoSPP = JSON.parse(localGetItem(education.EDU_DETAIL_INFO) || '{}');
  const UserEduStage = JSON.parse(localGetItem(education.EDUSTAGE) || '{}');

  const login_status = window.CallNativeMethod('get_login_status');

  const shortcuts = [
    {
      keyCode: 27,
      handle: (players, actions) => {
        if (players.isFullscreen) {
          actions.toggleFullscreen();
        }
      },
    },
  ];

  const InitData = useCallback(() => {
    setPlayer(null);
    EduVoideIsEnd = false;
    EduVoideIsReport = false;
    EduVoideCollected = false;
    EduVoidePlayingChapter = null;
    EduVoidePlayingChapterUrl = null;
  }, []);

  // 页面直接回退，正在播放的视频打桩
  useEffect(() => {
    return () => {
      const login_status = window.CallNativeMethod('get_login_status');
      // 正在播放回退页面，打桩
      if (login_status === '1' && player) {
        const playerState = player.getState();
        if (playerState && playerState.player && !playerState.player.paused) {
          dispatch(
            logging({
              category: 'R',
              action: 'Edu_Play_End',
              label: '',
              param2: {
                source: `Edu_Detail`,
                source_p: logInfoData.source_p,
                source_pp: logInfoData.source_pp,
                courseid: EduVoidePlayingChapterUrl && EduVoidePlayingChapterUrl.courseId,
                chapterid: EduVoidePlayingChapterUrl && EduVoidePlayingChapterUrl.chapterId,
                status: 'b',
                time: playerState.player.currentTime,
                age_period: UserEduStage,
              },
            }),
          );
        }
      }
    };
  }, [player, logInfoData]);
  // pv打桩，统计时长打桩
  useEffect(() => {
    dispatch(
      logging({
        category: 'P',
        action: 'Edu_Detail',
        label: '',
        param2: {
          source: `Edu_Detail`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          courseId,
          age_period: UserEduStage,
        },
      }),
    );
    dispatch(
      logPostInfo({
        source: 'Edu_Detail',
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
      }),
    );
  }, []);

  // pv打桩，统计时长打桩
  useEffect(() => {
    const initTime = +new Date();
    return () => {
      const leaveTime = +new Date();
      dispatch(
        logging({
          category: 'I',
          action: 'View_Time',
          label: '',
          param2: {
            source: `Edu_Detail`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            initTime,
            leaveTime,
            time: leaveTime - initTime,
            courseId,
            age_period: UserEduStage,
          },
        }),
      );
    };
  }, [logInfoData]);

  // 获取数据
  useEffect(() => {
    // 获取并存下登录状态
    const login_status = window.CallNativeMethod('get_login_status');
    // 当登录成功，并且进入页面状态为未登录，这刷新页面
    if (login_status === '1' && userInfo.st) {
      InitData();
      dispatch(getEducationDetail({ courseId, time: +new Date() }));
    } else if (login_status !== '1' && !userInfo.st) {
      InitData();
      dispatch(getEducationDetail({ courseId, time: +new Date() }));
    } else {
      dispatch(getEducationDetail({ courseId, time: +new Date() }));
    }
  }, [userInfo]);

  // 处理视频页面下架
  useEffect(() => {
    if (
      detailsData &&
      !detailsData.data &&
      detailsData.status === -2 &&
      detailsData.courseId === courseId
    ) {
      history.replace(`${CONTEXT}/erroredu?sourcePage=EduDetail`);
    }
  }, [detailsData]);

  useEffect(() => {
    if (
      detailsData &&
      detailsData.data &&
      detailsData.data.courseId &&
      detailsData.data.chapters &&
      detailsData.data.chapters.length > 0
    ) {
      EduVoideChapterList = detailsData.data.chapters;
    }
  }, [detailsData]);

  // 根据studyState初始化， 一进入，加载需要播放的视频地址
  useEffect(() => {
    // 获取并存下登录状态
    const login_status = window.CallNativeMethod && window.CallNativeMethod('get_login_status');
    if (
      detailsData &&
      detailsData.data &&
      detailsData.data.courseId &&
      detailsData.data.chapters &&
      detailsData.data.chapters.length > 0 &&
      detailsData.data.courseId === courseId
    ) {
      EduVoideChapterList = detailsData.data.chapters;
      if (login_status === '1') {
        // 视频是否已经收藏
        EduVoideCollected = detailsData.data.hasCollect === 1;

        const { chapters } = detailsData.data;
        let playingCha = chapters[0];
        let isPlaying = false;
        let isClick = false;

        if (!studyState || studyState === '0') {
          // 如果不是点击 继续学习或者重新学习 进入的详情页面
          for (let i = 0; i < chapters.length; i++) {
            if (chapters[i].status !== '2' && chapters[i].hasLastStudyChapter === 1) {
              playingCha = chapters[i];
              break;
            }
          }
          isPlaying = false;
        } else if (studyState === '1') {
          // 如果是点击 继续学习 进入的详情页面
          for (let i = 0; i < chapters.length; i++) {
            if (chapters[i].status !== '2' && chapters[i].hasLastStudyChapter === 1) {
              playingCha = chapters[i];
              break;
            }
          }
          isPlaying = true;
        } else if (studyState === '2') {
          // 如果是点击 重新学习 进入的详情页面
          isPlaying = true;
          isClick = true;
        } else {
          isPlaying = false;
        }
        // 一进来代码触发点击右边章节
        handleChapter(detailsData, playingCha, isPlaying, isClick);
      }
    }
  }, [detailsData]);

  // 当视频地址加载完成，控制是否播放，以及控制视频从什么地方开始播放
  useEffect(() => {
    // 获取并存下登录状态
    const login_status = window.CallNativeMethod('get_login_status');
    if (
      login_status === '1' &&
      detailsData &&
      detailsData.data &&
      player &&
      player.seek &&
      payUrlData &&
      isAutoPlay &&
      EduVoidePlayingChapter
    ) {
      // 免费、付费可试看、付费已购买走这个逻辑 控制开始播放
      if (
        payUrlData.playUrls &&
        payUrlData.playUrls.length > 0 &&
        (detailsData.data.pay === 0 ||
          (detailsData.data.pay === 1 &&
            (detailsData.data.hasBuy === 1 ||
              (detailsData.data.hasBuy === 0 && EduVoidePlayingChapter.isTry === 1))))
      ) {
        const chapterInfo = getChapterInfo(EduVoidePlayingChapter);
        let startTime = 0;
        // 如果是重新播放进入页面的，第一次从头开始播放，后面的记忆播放
        if (studyState === '2' && !chapterInfo.isPlayed) {
          // 如果是重新播放进入页面的，第一次从头开始播放，后面的记忆播放
          startTime = 0;
        } else if (parseInt(chapterInfo.status) === 1) {
          // 如果正在播放中，没有播放完，则继续播放
          startTime = parseInt(chapterInfo.playTime);
          // 如果以及播放完，则重头播放
        } else if (parseInt(chapterInfo.status) === 2 && EduVoideIsClick) {
          startTime = 0;
        }
        // 延迟处理开始播放，只为记录播放状态正确
        setTimeout(() => {
          player.seek && player.seek(startTime);
          player.play();
        }, 100);
      } else {
        player.play();
      }
      if (payUrlData.status === -2) {
        setIsNeedPay(true);
      }
    }
    EduVoidePlayingChapterUrl = payUrlData;
  }, [detailsData, payUrlData, player, isAutoPlay]);

  // 注册订阅视频事件处理
  useEffect(() => {
    const login_status = window.CallNativeMethod('get_login_status');
    if (
      login_status === '1' &&
      player &&
      player.actions &&
      detailsData &&
      detailsData.data &&
      detailsData.data.courseId &&
      courseId === detailsData.data.courseId
    ) {
      const reportTime = 5;
      // 记录上次记录的时间
      let currentTimeOld = 0;
      // 开始播放的时间
      let startPlayTime = 0;
      // 记录上次记录的时间 内存中2秒一次记录
      let currentTimeOld_ = 0;
      player.subscribeToStateChange((state, prevState) => {
        // 监听播放暂停
        if (state.paused !== prevState.paused) {
          setIsPause(state.paused);
        }
        // 开始播放统计
        if (!state.paused && prevState.paused) {
          const currentTime = parseInt(state.currentTime);
          startPlayTime = +new Date();
          EduVoideIsEnd = false;
          playTimeReport(currentTime, 1, detailsData, true, startPlayTime);
          logNativeVideoInfo(`视频 开始播放`, {
            currentTime,
            duration: state.duration,
            courseid: EduVoidePlayingChapterUrl.courseid,
            chapterId: EduVoidePlayingChapterUrl.chapterId,
          });
        }
        // 结束播放统计 排除播放完毕
        if (state.paused && !prevState.paused && state.currentTime !== state.duration) {
          const currentTime = parseInt(state.currentTime);
          playTimeReport(currentTime, 2, detailsData, true, startPlayTime);
          logNativeVideoInfo(`视频 播放暂停`, {
            currentTime,
            duration: state.duration,
            courseid: EduVoidePlayingChapterUrl.courseid,
            chapterId: EduVoidePlayingChapterUrl.chapterId,
          });
        }

        // 播放中 统计每一秒1s一次本地存储
        if (
          +new Date() - startPlayTime > 2000 &&
          Math.abs(state.currentTime - currentTimeOld_) > 2
        ) {
          const currentTime = parseInt(state.currentTime);
          currentTimeOld_ = currentTime;
          // 记录播放进度
          recordVoidePlayProgress(getChapterInfo(EduVoidePlayingChapter), currentTime, 2);
        }
        // 播放中进度, 播放一秒后开始判断是否需要统计进度
        if (
          +new Date() - startPlayTime > 2000 &&
          Math.abs(state.currentTime - currentTimeOld) > reportTime
        ) {
          const currentTime = parseInt(state.currentTime);
          currentTimeOld = currentTime;
          playTimeReport(currentTime, 2, detailsData, false, startPlayTime);
        }

        // 播放结束统计 state.duration存在则为正常播放结束
        if (state.ended && state.duration) {
          const currentTime = parseInt(state.currentTime);
          if (!EduVoideIsEnd) {
            EduVoideIsEnd = true;
            playTimeReport(currentTime, 3, detailsData, true, startPlayTime);
            logNativeVideoInfo(`视频 播放结束`, {
              currentTime,
              duration: state.duration,
              courseid: EduVoidePlayingChapterUrl.courseid,
              chapterId: EduVoidePlayingChapterUrl.chapterId,
            });
          }
        }
      });
    }
  }, [player, detailsData]);

  // 记录视频日志
  const logNativeVideoInfo = useCallback((text, info) => {
    logNative(`${text} ${JSON.stringify(info)}`, info, true);
  }, []);

  // 播放数据打桩处理以及记录 type: 1-章节开始播放上报，2-章节播放时长上报，3-章节播放完成上报
  const playTimeReport = useCallback(
    (playTime, type, detail, isLoging, startPlayTime) => {
      // 记录播放进度
      recordVoidePlayProgress(getChapterInfo(EduVoidePlayingChapter), playTime, type);

      // 继续播放下一个视频
      if (type === 3) {
        judgePlayNext(detail);
      }
      // 避免同一个状态下重复上报
      if (EduVoideIsReport === type) {
        setTimeout(() => {
          EduVoideIsReport = false;
        }, 50);
        return;
      }
      EduVoideIsReport = type;
      dispatch(
        postEducationPlayDataReport({
          type,
          playTime,
          courseId: EduVoidePlayingChapterUrl.courseId,
          chapterId: EduVoidePlayingChapterUrl.chapterId,
        }),
      );

      if (isLoging && EduVoidePlayingChapterUrl.status !== -2) {
        if (type === 1) {
          dispatch(
            logging({
              category: 'R',
              action: 'Edu_Play_Start',
              label: '',
              param2: {
                source: `Edu_Detail`,
                source_p: logInfoData.source_p,
                source_pp: logInfoData.source_pp,
                courseid: EduVoidePlayingChapterUrl.courseId,
                chapterid: EduVoidePlayingChapterUrl.chapterId,
                st: playTime,
                time: playTime,
                age_period: UserEduStage,
                startPlayTime,
              },
            }),
          );
        } else {
          dispatch(
            logging({
              category: 'R',
              action: 'Edu_Play_End',
              label: '',
              param2: {
                source: `Edu_Detail`,
                source_p: logInfoData.source_p,
                source_pp: logInfoData.source_pp,
                courseid: EduVoidePlayingChapterUrl.courseId,
                chapterid: EduVoidePlayingChapterUrl.chapterId,
                status: type === 3 ? 's' : 'p',
                time: playTime,
                age_period: UserEduStage,
                startPlayTime,
                endPayTime: +new Date(),
                payTime: +new Date() - startPlayTime,
              },
            }),
          );
        }
      }
    },
    [dispatch],
  );

  //  记录播放进度
  const recordVoidePlayProgress = useCallback((chapter, playTime, type) => {
    const chapterInfo = getChapterInfo(chapter);
    chapterInfo.isPlayed = true;
    if (type === 1) {
      chapterInfo.status = 1; // 学习中
    } else if (type === 2) {
      chapterInfo.status = 1; // 学习中
      chapterInfo.playTime = playTime;
    } else if (type === 3) {
      chapterInfo.status = 2; // 已学完
      chapterInfo.playTime = playTime;
    }
    updateChapterList(chapterInfo);
  });

  // 判断是否播放下一个章节
  const judgePlayNext = useCallback(detail => {
    let index = 0;
    const chapters = EduVoideChapterList;

    chapters.map((item, i) => {
      if (item.chapterId === EduVoidePlayingChapterUrl.chapterId) {
        index = i;
      }
    });
    while (
      index < chapters.length - 1 &&
      chapters[index] &&
      parseInt(chapters[index].status) === 2
    ) {
      index += 1;
    }

    if (index === chapters.length - 1 && parseInt(chapters[index].status) === 2) {
      logNativeVideoInfo(`课程最后可播放章节播放完毕`, {
        courseid: EduVoidePlayingChapterUrl.courseid,
        chapterId: EduVoidePlayingChapterUrl.chapterId,
      });
      if (studyState !== '3') {
        setIsAllVideoEnd(true);
      }
    } else if (chapters[index]) {
      setTimeout(() => {
        handleChapter(detail, chapters[index], true);
      }, 100);
    }
  }, []);

  /**
   * detail: 详情，chapter：选中的课程，是否直接开始播放autoPlay
   * isClick: 是否为用户点击章节播放
   */
  const handleChapter = useCallback(
    (detail, chapter, autoPlay, isClick) => {
      setIsNeedPay(false);
      // 获取用户是否登录
      const login_status = window.CallNativeMethod && window.CallNativeMethod('get_login_status');
      if (login_status !== '1') {
        // 未登录，弹出登录框
        window.CallNativeMethod('login');
        return;
      }
      if (isClick) {
        // 如果点击这个1.5秒内EduVoideIsClick值为true
        EduVoideIsClick = true;
        setTimeout(() => {
          EduVoideIsClick = false;
        }, 1500);
        // 处理当正在播放视频，点击了其他章节，先暂停正在播放的章节
        if (player) {
          const playerState = player.getState();
          if (playerState && playerState.player && !playerState.player.paused) {
            player.pause();
          }
        }
      }
      // 延时请求点击的章节, 只为了等待视频结束播放
      setTimeout(() => {
        dispatch(
          getEducationPlayUrl({
            type: 1,
            courseId: detail.data.courseId,
            chapterId: chapter.chapterId,
          }),
        );
        EduVoidePlayingChapter = { ...chapter, courseId: detail.data.courseId };
        if (autoPlay) {
          setIsAutoPlay(true);
        }
      }, 50);
    },
    [player],
  );

  // 点击章节打桩
  const handleChapterForLoging = useCallback(() => {
    // 获取用户是否登录
    const login_status = window.CallNativeMethod && window.CallNativeMethod('get_login_status');
    if (login_status !== '1') {
      // 未登录，弹出登录框
      window.CallNativeMethod('login');
      return;
    }
    dispatch(
      logging({
        category: 'C',
        action: 'Edu_Course_Play',
        label: '',
        param2: {
          source: `Edu_Detail`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          courseid: EduVoidePlayingChapterUrl.courseId,
          chapterid: EduVoidePlayingChapterUrl.chapterId,
          type: 'clickItem',
          age_period: UserEduStage,
        },
      }),
    );
  }, []);

  // 点击播放按钮
  const handelPlay = useCallback(() => {
    // 获取用户是否登录
    const login_status = window.CallNativeMethod && window.CallNativeMethod('get_login_status');
    if (login_status !== '1') {
      // 未登录，弹出登录框
      window.CallNativeMethod('login');
      return;
    }
    if (!player) {
      return;
    }
    setIsAutoPlay(true);
    const playerState = player.getState();
    if (playerState && playerState.player) {
      if (playerState.player.paused) {
        player.play();
      } else {
        player.pause();
      }
    }
    dispatch(
      logging({
        category: 'C',
        action: 'Edu_Course_Play',
        label: '',
        param2: {
          source: `Edu_Detail`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          courseid: EduVoidePlayingChapterUrl.courseId,
          chapterid: EduVoidePlayingChapterUrl.chapterId,
          type: 'clickS',
          age_period: UserEduStage,
        },
      }),
    );
  }, [player, logInfoData]);

  // 点击收藏
  const handleCollect = useCallback(() => {
    // 获取用户是否登录
    const login_status = window.CallNativeMethod && window.CallNativeMethod('get_login_status');
    if (login_status === '1') {
      if (!EduVoideCollected) {
        EduVoideCollected = true;
        dispatch(
          postEducationCollectReport({
            type: 10,
            courseId: detailsData.data.courseId,
          }),
        );

        dispatch(
          logging({
            category: 'C',
            action: 'Edu_Course_Collect',
            label: '',
            param2: {
              source: `Edu_Detail`,
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              courseid: detailsData.data.courseId,
              age_period: UserEduStage,
            },
          }),
        );
      }
    } else {
      // 未登录，弹出登录框
      window.CallNativeMethod('login');
      // 点击 登录
      dispatch(
        logging({
          category: 'C',
          action: 'Login',
          label: '',
          param2: {
            source: 'Edu_Detail',
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            courseid: detailsData.data.courseId,
            age_period: UserEduStage,
          },
        }),
      );
    }
  }, [dispatch, logInfoData, detailsData]);

  const getChapterInfo = chapter => {
    const chapterInfo = EduVoideChapterList.find(item => {
      return item.chapterId === chapter.chapterId;
    });
    return chapterInfo;
  };
  const updateChapterList = chapter => {
    for (let i = 0; i < EduVoideChapterList.length; i++) {
      if (EduVoideChapterList[i].chapterId === chapter.chapterId) {
        EduVoideChapterList[i] = chapter;
        break;
      }
    }
  };

  // 格式化时间
  const formatDate = time_ => {
    let time = parseInt(time_);
    let timeStr = '';
    if (parseInt(time / 3600) > 0) {
      if (parseInt(time / 3600)) {
        timeStr += `${parseInt(time / 3600)}小时`;
      }
      if (parseInt((time % 3600) / 60)) {
        timeStr += `${parseInt((time % 3600) / 60)}分`;
      }
    } else {
      if (parseInt((time % 3600) / 60)) {
        timeStr += `${parseInt((time % 3600) / 60)}分`;
      }
      if (parseInt((time % 3600) % 60)) {
        timeStr += `${parseInt((time % 3600) % 60)}秒`;
      }
    }
    return timeStr;
  };

  // 格式化数字
  const formatNum = num_ => {
    let num = parseInt(num_);
    let numStr = num;
    if (num > 10000) {
      numStr = `${(num / 10000).toFixed(1)}万`;
    }
    return numStr;
  };

  const handleSource = useCallback(supplyInfo => {
    window.open(supplyInfo.targetContent.url);
  }, []);

  const handleReplay = useCallback(() => {
    const { chapters } = detailsData.data;
    const playing = chapters[0];
    // 重新播放 模拟点击第一个章节
    handleChapter(detailsData, playing, true, true);
  }, [detailsData]);

  const handleBuy = useCallback(
    type => {
      // 获取用户是否登录
      const login_status = window.CallNativeMethod && window.CallNativeMethod('get_login_status');
      if (login_status !== '1') {
        // 未登录，弹出登录框
        window.CallNativeMethod('login');
        return;
      }
      setPayVisible(true);
      dispatch(
        logging({
          category: 'C',
          action: 'Edu_Buy',
          label: '',
          param2: {
            source: 'Edu_Detail',
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            courseid: detailsData.data.courseId,
            type,
          },
        }),
      );
    },
    [detailsData],
  );

  const handleColse = useCallback(flag => {
    setPayVisible(false);
    if (flag === 'success') {
      InitData();
    }
  }, []);

  if (
    !detailsData ||
    !detailsData.data ||
    !detailsData.data.courseId ||
    (detailsData.data && detailsData.data.courseId && courseId !== detailsData.data.courseId)
  ) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.page_container}>
        <div className={`video ${styles.video}`}>
          <div className={`video_player`}>
            <Player
              src={`${payUrlData && payUrlData.playUrls && payUrlData.playUrls[0].playUrl}`}
              ref={p => setPlayer(p)}
              poster={detailsData.data.imgUrl}
              className={`video_react_video ${styles.detail_swiper_video}`}
              fluid={false}
              playsInline={true}
              autoPlay={false}
              muted={true}
              loop={false}
              width={`100%`}
              height={`100%`}
            >
              <Shortcut clickable={true} dblclickable={true} shortcuts={shortcuts} />
              <BigPlayButton position="center" className={styles.player_btn} />
              <ControlBar autoHide={true}>
                <PlayToggle />
                <VolumeMenuButton vertical />
                <PlaybackRateMenuButton rates={[2, 1.5, 1.25, 1, 0.5]} />
              </ControlBar>
            </Player>
            {isPause && !EduVoideIsEnd && (
              <div
                className={`video_btn_pause ${!isPause ? styles.icon_v_play : styles.icon_v_stop}`}
                onClick={handelPlay}
              />
            )}
            {payUrlData && payUrlData.loading && login_status === '1' && (
              <div className={`video_loading`}>正在加载...</div>
            )}
            {EduVoideIsEnd && isAllVideoEnd && login_status === '1' && (
              <div className={`video_replay`}>
                <div className="video_replay_text">该课程最后一节已学完 </div>
                <div
                  className="video_replay_btn"
                  onClick={() => {
                    handleReplay();
                  }}
                >
                  重新学习
                </div>
              </div>
            )}
            {isNeedPay && login_status === '1' && (
              <div className={`video_pay`}>
                <div className="video_pay_text">该课程需购买后观看</div>
                <div
                  className="video_pay_btn"
                  onClick={() => {
                    handleBuy('cover');
                  }}
                >
                  立即购买
                </div>
              </div>
            )}
          </div>

          <div className="video_chapter">
            <div className="chapter_top">
              <div className="top_title">章节</div>
              <div className="top_info">
                共{EduVoideChapterList && EduVoideChapterList.length}节
              </div>
            </div>
            <div className="chapter_list">
              {EduVoideChapterList &&
                EduVoideChapterList.map((item, i) => {
                  return (
                    <div
                      className={`${
                        EduVoidePlayingChapter &&
                        EduVoidePlayingChapter.chapterId === item.chapterId
                          ? `chapter_item active`
                          : `chapter_item `
                      }`}
                      key={i}
                      onClick={() => {
                        handleChapter(detailsData, item, true, true);
                        handleChapterForLoging(detailsData, item);
                      }}
                    >
                      {detailsData.data.hasBuy === 0 && item.isTry === 1 && (
                        <div className="chapter_item_try" />
                      )}
                      <div className="chapter_item_top">
                        <div className="chapter_item_title" title={item.chapterName}>
                          {item.chapterName}
                        </div>
                      </div>
                      <div className="chapter_item_info">
                        <div className="chapter_item_time">
                          <span className="chapter_item_icon" />
                          <span>{formatDate(item.lengthOfTime)}</span>
                        </div>
                        {/* 当前没有播放, 且状态为已学完 */}
                        {parseInt(item.status) === 2 &&
                          login_status === '1' &&
                          (isPause ||
                            (EduVoidePlayingChapterUrl &&
                              EduVoidePlayingChapterUrl.chapterId !== item.chapterId)) && (
                            <div className="chapter_item_end">已学完</div>
                          )}
                        {/* 当前没有播放, 且状态为学习中  */}
                        {parseInt(item.status) === 1 &&
                          parseInt(item.playTime) > 0 &&
                          login_status === '1' &&
                          (isPause ||
                            (EduVoidePlayingChapterUrl &&
                              EduVoidePlayingChapterUrl.chapterId !== item.chapterId)) && (
                            <div className="chapter_item_played">
                              已学习
                              {formatDate(item.playTime)}
                            </div>
                          )}
                        {/* 当前正在播放, 且状态为学习中  */}
                        {parseInt(item.status) === 1 &&
                          !isPause &&
                          login_status === '1' &&
                          EduVoidePlayingChapterUrl &&
                          EduVoidePlayingChapterUrl.chapterId === item.chapterId && (
                            <div className="chapter_item_play">学习中</div>
                          )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
        <div className="infos">
          <div className="infos_d1">
            <div className="d1_left">
              <div className="d1_left_title" title={detailsData.data.name}>
                {detailsData.data.name}
              </div>
              <div className="d1_left_desc" title={detailsData.data.shortDesc}>
                {detailsData.data.shortDesc}
              </div>
              <div className="d1_left_other">
                <div className="d1_left_other_item">
                  <div className="item_icon_study" />
                  <div className="item_text">{formatNum(detailsData.data.playerNum)}人在学</div>
                </div>
                <div className="d1_left_other_item">
                  <div className="item_icon_collect" />
                  <div className="item_text">
                    {formatNum(
                      EduVoideCollected && detailsData.data.hasCollect === 0
                        ? detailsData.data.collectNum + 1
                        : detailsData.data.collectNum,
                    )}
                    人收藏
                  </div>
                </div>
                <div className="d1_left_other_item">
                  <div className="item_icon_money" />
                  {detailsData.data.pay !== 1 ? (
                    <div className="item_text">免费</div>
                  ) : (
                    <div className="item_text">{formatNum(detailsData.data.buyNum)}人购买</div>
                  )}
                </div>
              </div>
            </div>
            {detailsData.data.pay === 1 && (
              <div className="d1_center">
                {detailsData.data.hasBuy !== 1 && (
                  <>
                    {detailsData.data.discountPrice > 0 && detailsData.data.price && (
                      <>
                        <div className="d1_discount_price">
                          <span className="d1_discount_price_mark">￥</span>
                          {(detailsData.data.discountPrice / 100).toFixed(2)}
                        </div>
                        <div className="d1_price">{(detailsData.data.price / 100).toFixed(2)}</div>
                      </>
                    )}
                    {detailsData.data.discountPrice === 0 && detailsData.data.price && (
                      <div className="d1_discount_price">
                        <span className="d1_discount_price_mark">￥</span>
                        {(detailsData.data.price / 100).toFixed(2)}
                      </div>
                    )}
                  </>
                )}

                {detailsData.data.hasBuy === 1 ? (
                  <div className="d1_pay_btn_">已购买</div>
                ) : (
                  <div
                    className="d1_pay_btn"
                    onClick={() => {
                      handleBuy('introduce');
                    }}
                  >
                    购买课程
                  </div>
                )}
              </div>
            )}
            <div className="d1_right">
              {detailsData.data.hasCollect === 1 || EduVoideCollected ? (
                <div className="d1_right_collected_btn">
                  <span className="icon iconfont icon-collected collect_btn" />
                  <span>已收藏</span>
                </div>
              ) : (
                <div className="d1_right_collect_btn" onClick={handleCollect}>
                  <span className="icon iconfont icon-collect collect_btn" />
                  <span>收藏</span>
                </div>
              )}
            </div>
          </div>
          <div className="infos_d2">
            <div className="d2_title">课程信息</div>
            <div className="d2_list">
              {detailsData.data.chapterNum && (
                <div className="d2_item">
                  <div className="d2_item_title">章节：</div>
                  <div className="d2_item_text">共{detailsData.data.chapterNum}节</div>
                </div>
              )}

              {detailsData.data.teachers && (
                <div className="d2_item">
                  <div className="d2_item_title">教师：</div>
                  <div className="d2_item_text">{detailsData.data.teachers}</div>
                </div>
              )}
              {detailsData.data.supplyInfo && detailsData.data.supplyInfo.targetType === `2` && (
                <div className="d2_item">
                  <div className="d2_item_title">来源：</div>
                  <div
                    className="d2_item_link"
                    onClick={() => {
                      handleSource(detailsData.data.supplyInfo);
                    }}
                  >
                    {detailsData.data.supplyInfo.text}
                  </div>
                </div>
              )}
              {detailsData.data.supplyInfo && detailsData.data.supplyInfo.targetType === `1` && (
                <div className="d2_item">
                  <div className="d2_item_title">来源：</div>
                  <div className="d2_item_app">
                    <SoftCardEdu
                      soft={detailsData.data.supplyInfo.softInfo}
                      logInfo={{ ...logInfoData, source: 'EduDetail' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="infos_d3">
            <div className="d3_title">课程简介</div>
            <div
              className="d3_content"
              dangerouslySetInnerHTML={{
                __html: detailsData.data.courseIntro,
              }}
            />
          </div>
        </div>
        {payVisible && <EduDetailPay detailsData={detailsData} handleClose={handleColse} />}
      </div>
    </div>
  );
}
